/**
 * The two ends of the sidebar: the product at the top with the control that
 * folds the column, and in the footer the brand you are working in with the
 * way to another one.
 *
 * THE PRODUCT AT THE TOP, THE BRAND AT THE FOOT. The owner, 2026-10-12: the top
 * left should not say FirmAdapt, it should be the name of the product, because
 * the brand is already at the bottom. So the column starts with the product's
 * mark and name, a way home, and `WorkspaceSwitcher` in the footer is the one
 * control that switches brand and, for a super admin, turns on the view of
 * every brand at once. Until then the brand sat here and a view switcher with
 * the product mark sat in the footer for super admins only.
 *
 * THE BRAND'S COLOUR IS NOT DECORATION. `BrandEdge` below is the 3px rule that
 * used to sit under the top bar. Switching brand re-scopes every number on
 * screen, so the one thing an operator must never have to read a label to know
 * is which brand they are about to send from. It is the sidebar's right edge,
 * and on a phone, where there is no sidebar, it is the top edge of the shell
 * instead. One component draws both so the two cannot drift apart. The brand's
 * mark is the face of the footer switcher at both widths, so the folded rail
 * still shows it.
 *
 * THE COLLAPSE SITS ON THE TOP ROW, at its right hand end, which is where the
 * reference puts it. In the slide-over the same slot closes it.
 *
 * THE WORKSPACE MENU SWITCHES, THE WINDOW BEHIND IT MANAGES. Picking a brand is
 * an hourly action and stays one click, in a menu that opens over the footer.
 * Renaming, deleting and creating are rare, they need a form, and they live in
 * `WorkspaceModal`, which the last row of the menu opens and which answers to
 * `?modal=workspace` in the address bar.
 */
import { useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { cn } from '../lib/cn'
import { brandTone } from '../lib/brandTone'
import { useBrandSwitch, useSession, useViewSwitch } from '../lib/session'
import type { SessionBrand } from '../lib/types'
import { BrandMark } from './Identity'
import { Logo } from './Logo'
import { KeyCaps } from '../pages/queue/keyboard'
import { Menu, type MenuSection } from './Menu'
import { Spinner } from './Spinner'
import { Tooltip } from './Tooltip'
import { useToast } from './Toast'
import { WorkspaceModal } from './WorkspaceModal'
import { shortcutById } from '../lib/shortcuts'
import { RAIL_IDLE, RAIL_ITEM } from './CollapsedRail'
import { useOverlayParam } from '../lib/url-state'
import { holdsPath, VIEW_COPY, viewSwitcherState } from './sidebar-model'
import type { NavItem } from './Sidebar'
import { IconBrands, IconChevronRight, IconClose, IconSelector, IconSettings } from './icons'

/**
 * The brand's own colour as an edge.
 *
 * Vertical down the sidebar from 1024px up, horizontal across the top of the
 * shell below that. The horizontal one is not a top bar coming back: it is
 * three pixels of colour with nothing in it, and without it the wrong-brand
 * guard would simply not exist on a phone, which is where somebody is most
 * likely to be tapping Send without reading first.
 */
export function BrandEdge({
  orientation,
  className,
}: {
  orientation: 'vertical' | 'horizontal'
  className?: string
}) {
  const { activeBrand } = useSession()
  const tone = brandTone(activeBrand?.id)
  return (
    <div
      aria-hidden="true"
      title={activeBrand ? `Working in ${activeBrand.name}` : undefined}
      className={cn(
        tone.bar,
        orientation === 'vertical' ? 'absolute inset-y-0 right-0 w-[3px]' : 'h-[3px] w-full shrink-0',
        className,
      )}
    />
  )
}

export function SidebarWorkspace({
  collapsed = false,
  onToggleCollapsed,
  onClose,
}: {
  collapsed?: boolean
  /** Absent in the mobile slide-over, where there is nothing to collapse into. */
  onToggleCollapsed?: () => void
  /** Present only in the slide-over, where the same slot closes it instead. */
  onClose?: () => void
}) {
  if (collapsed) {
    return (
      <div className="flex flex-col items-center gap-1 pb-2">
        {onToggleCollapsed === undefined ? null : <CollapseButton collapsed onToggle={onToggleCollapsed} />}
        <ProductName collapsed />
      </div>
    )
  }

  return (
    // The reference's 20 above and 12 below. The 15 at each side is the
    // column's own inset, so it is not repeated here.
    <div className="flex shrink-0 items-center gap-1 pb-3 pt-5">
      <ProductName collapsed={false} />
      {onToggleCollapsed === undefined ? null : <CollapseButton collapsed={false} onToggle={onToggleCollapsed} />}
      {onClose === undefined ? null : (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close navigation"
          title="Close navigation"
          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ink-subtle transition hover:bg-surface-hover-strong hover:text-ink-secondary"
        >
          <IconClose className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}

/** The product's name, said where the column starts. */
export const PRODUCT_NAME = 'Son of Basel'

/**
 * The product's mark and name, a way home.
 *
 * The owner, 2026-10-12: "it shouldn't be FirmAdapt here. It should just be
 * the name of the product itself ... on the top left. Because we already have
 * this at the bottom." The brand moved to the footer, where the switcher for
 * it and for the every-brand view now sit in one control
 * (`WorkspaceSwitcher`), and the column starts with what the app is.
 */
function ProductName({ collapsed }: { collapsed: boolean }) {
  return (
    <Link
      to="/inbox"
      aria-label={collapsed ? PRODUCT_NAME : undefined}
      title={collapsed ? PRODUCT_NAME : undefined}
      className={cn(
        'flex items-center rounded-lg transition focus-visible:outline-none focus-visible:shadow-focus',
        collapsed ? cn(RAIL_ITEM, RAIL_IDLE) : 'h-10 min-w-0 flex-1 gap-2 px-2 hover:bg-surface-hover-strong',
      )}
    >
      <Logo className="h-6 w-6 shrink-0" />
      {collapsed ? null : (
        /*
          THE LARGEST WORDS IN THE COLUMN. The reference sets the top name at
          16/24 semibold, a full step above everything under it.
        */
        <span className="min-w-0 flex-1 truncate text-left text-title font-semibold text-ink">{PRODUCT_NAME}</span>
      )}
    </Link>
  )
}

/**
 * The fold, with its key printed beside it.
 *
 * The caps are read off the registry row the binding comes from rather than
 * typed here, which is what the shortcuts drawer does too: a caption that can
 * be written twice is a caption that can be wrong. The bubble carries the key
 * and the button keeps its `aria-label`, so the name a screen reader announces
 * is unchanged and the key is an extra rather than part of the name.
 */
function CollapseButton({
  collapsed,
  onToggle,
  className,
}: {
  collapsed: boolean
  onToggle: () => void
  className?: string
}) {
  const label = collapsed ? 'Expand the navigation' : 'Collapse the navigation'
  return (
    <Tooltip
      className={className}
      content={
        <span className="flex items-center gap-2">
          {label}
          <KeyCaps binding={shortcutById('sidebar.collapse').binding} />
        </span>
      }
    >
      <button
        type="button"
        onClick={onToggle}
        aria-label={label}
        className={
          collapsed
            ? cn(RAIL_ITEM, RAIL_IDLE)
            : 'grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ink-subtle transition hover:bg-surface-hover-strong hover:text-ink-secondary'
        }
      >
        {collapsed ? <IconChevronRight className="h-[1.25rem] w-[1.25rem]" /> : <IconPanelCollapse className="h-[1.125rem] w-[1.125rem]" />}
      </button>
    </Tooltip>
  )
}

/**
 * A panel folding to the left, drawn here rather than in the icon set.
 *
 * `icons.tsx` is shared by every screen and is edited from several directions
 * at once, so the one glyph this control needs is local rather than racing
 * another change into a file nobody owns. The chevron the collapse used to wear
 * was fine at the bottom of the column, where it pointed at the edge it folded
 * towards. Up here beside the workspace name it would be the second chevron on
 * the row and would read as another dropdown, which is the one thing it must
 * not be mistaken for. Same 24 grid and stroke weight as the rest of the set.
 */
function IconPanelCollapse({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={cn('h-[1.15em] w-[1.15em] shrink-0', className)}
    >
      <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
      <path d="M10 4.5v15M7.5 9.5 5.5 12l2 2.5" />
    </svg>
  )
}

/**
 * The brand you are working in, the way to another one, the every-brand view
 * for a super admin, and the way to the window that renames, deletes and
 * creates brands. In the footer, the slot Salesforge switches products in.
 *
 * ONE CONTROL FOR ONE QUESTION. Until 2026-10-21 the brand was switched at the
 * top of the column and the view (this brand, or every brand at once) at its
 * foot, by super admins only. The owner asked for the product's name at the top
 * and the brand at the bottom (2026-10-12), so the two now share the footer
 * control, and everybody has it: somebody who is not a super admin sees their
 * brands and nothing else. Picking a brand while the every-brand view is on
 * turns the view off, because picking one brand is the act of focusing on it.
 *
 * No search box over the list: this is a handful of brands for a handful of
 * people, and a filter over four rows is furniture. The rows are the portalled
 * `Menu`, so the arrow keys and focus work from the keyboard.
 */
export function WorkspaceSwitcher({
  collapsed = false,
  ownsModal,
  platformItems,
}: {
  collapsed?: boolean
  /** Whether this copy of the column mounts the workspace window. See below. */
  ownsModal: boolean
  /** The every-brand screens, handed in so this file holds no second copy of the list. */
  platformItems: readonly NavItem[]
}) {
  const { brands, activeBrand, isAdmin, isSuperAdmin, superAdminView } = useSession()
  const switchBrand = useBrandSwitch()
  const changeView = useViewSwitch()
  const toast = useToast()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  /*
   * WHICH COPY OF THE COLUMN OWNS THE WORKSPACE WINDOW. Below 1024px there are
   * two sidebars mounted at once, the hidden one in the layout and the one in
   * the slide-over, and the window opens from the address bar, so both would
   * see `?modal=workspace` and two windows would stack. The one in the layout
   * is always mounted, and a portal does not care that its parent is hidden,
   * so that one owns it and the slide-over only writes the address.
   */
  const manage = useOverlayParam('modal', 'workspace')
  const triggerRef = useRef<HTMLButtonElement>(null)

  // While a view change is in flight the control shows where it is going, so
  // a second pick does not read the old state and send the same thing again.
  const viewOn = isSuperAdmin && (changeView.isPending ? changeView.variables === 'super_admin' : superAdminView)
  const state = viewSwitcherState(viewOn, pathname, platformItems)
  const busy = switchBrand.isPending || changeView.isPending

  const setView = (next: boolean, then?: () => void) => {
    if (changeView.isPending) return
    if (next === superAdminView) {
      then?.()
      return
    }
    changeView.mutate(next ? 'super_admin' : 'admin', {
      onSuccess: () => {
        toast.success(
          next ? 'Every brand at once' : 'One brand',
          next ? 'The every-brand screens are open to you.' : 'You now see the console as any other admin does.',
        )
        then?.()
      },
      onError: (error) => toast.error('Could not switch the view', error),
    })
  }

  const switchTo = (brand: SessionBrand) => {
    if (brand.id === activeBrand?.id) return
    switchBrand.mutate(brand.id, {
      onSuccess: () => toast.success(`Now showing ${brand.name}`, 'Everything on screen is scoped to this brand.'),
      onError: (error) => toast.error('Could not switch brand', error),
    })
  }

  const choose = (brand: SessionBrand) => {
    if (!viewOn) {
      switchTo(brand)
      return
    }
    // Leaving an every-brand screen as well as the view, because that screen
    // is refused outside the view and would answer with a wall.
    setView(false, () => {
      if (holdsPath(platformItems, pathname)) navigate('/inbox')
      switchTo(brand)
    })
  }

  /*
   * Built inline on every render rather than memoised. `Menu` is written for
   * exactly that: it reads the rows through a ref so a fresh array cannot
   * re-run its placement effect.
   */
  const sections: MenuSection[] = [
    {
      id: 'brands',
      label: 'Working on',
      items:
        brands.length === 0
          ? [
              {
                id: 'none',
                label: 'No workspace yet',
                // Stated rather than hidden. An empty menu reads as a broken
                // control. This one says who can fix it.
                description: 'You are not a member of any brand yet. An admin can add you to one.',
                disabled: true,
              },
            ]
          : brands.map((brand) => ({
              id: brand.id,
              label: brand.name,
              icon: <BrandMark brandId={brand.id} name={brand.name} logoSvg={brand.logoSvg} size="sm" />,
              checked: !viewOn && brand.id === activeBrand?.id,
              description:
                brand.knowledgeValid === false
                  ? 'Brand file needs fixing before this brand can launch anything'
                  : undefined,
              descriptionTone: 'warning' as const,
              onSelect: () => choose(brand),
            })),
    },
    ...(isSuperAdmin
      ? [
          {
            id: 'platform',
            label: VIEW_COPY.platform,
            items: platformItems.map((item) => ({
              id: item.to,
              label: item.label,
              description: item.hint,
              icon: <item.icon className="h-4 w-4" />,
              iconChip: true,
              checked: state.checked === item.to,
              onSelect: () => setView(true, () => navigate(item.to)),
            })),
          },
        ]
      : []),
    {
      id: 'manage',
      items: [
        {
          id: 'manage-open',
          label: 'Manage workspaces',
          description: 'Rename one, delete one, or make another',
          icon: <IconSettings className="h-4 w-4" />,
          iconChip: true,
          onSelect: () => manage.open(),
        },
      ],
    },
  ]

  const name = viewOn ? state.label : (activeBrand?.name ?? 'No brand')

  return (
    <div className={cn(collapsed ? 'mt-2 flex justify-center' : 'mt-3')}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={collapsed ? `Switch workspace: ${name}` : undefined}
        title={collapsed ? name : 'Switch workspace'}
        className={cn(
          'flex items-center transition',
          collapsed
            ? cn(RAIL_ITEM, RAIL_IDLE)
            : 'h-10 w-full gap-2 rounded-lg border border-line-strong bg-surface px-2.5 shadow-xs hover:bg-surface-hover',
        )}
      >
        {viewOn ? (
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md bg-surface-muted text-ink-subtle">
            <IconBrands className="h-4 w-4" />
          </span>
        ) : (
          <BrandMark
            brandId={activeBrand?.id}
            name={activeBrand?.name ?? 'No brand'}
            logoSvg={activeBrand?.logoSvg}
            size="sm"
          />
        )}
        {collapsed ? null : (
          <>
            <span className="min-w-0 flex-1 truncate text-left text-base font-semibold text-ink-secondary">{name}</span>
            {busy ? <Spinner size="sm" label="Switching" /> : <IconSelector className="h-4 w-4 shrink-0 text-ink-faint" />}
          </>
        )}
      </button>

      <Menu
        open={open}
        onClose={() => setOpen(false)}
        triggerRef={triggerRef}
        label="Switch workspace"
        sections={sections}
        placement="top"
        width={288}
        footer={
          <>
            Picking a brand re-scopes every list and chart. Each sending account you own carries its own
            limits and they hold across all brands, so switching never gives you more room.
          </>
        }
      />

      {/*
        The window the last row opens. Open is `?modal=workspace`, so a link
        opens it and Back closes it.
      */}
      <WorkspaceModal
        open={ownsModal && manage.isOpen}
        onClose={manage.close}
        brands={brands}
        currentBrandId={activeBrand?.id ?? null}
        canManage={isAdmin}
      />
    </div>
  )
}
